import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { APIError } from '~/src/utils/error';
import { useCurrentOrgFprint } from '../../user';
import { fieldMappingService } from '../services';
import { Project } from '../../project';
import {
  createOptimisticFieldMappingUpdate,
  rollbackOptimisticUpdate,
} from './useOptimisticFieldMappingUpdate';

type DeleteFieldMappingVariables = {
  projectId: number;
  mappingId: number;
  clearValue?: boolean;
};

type DeleteFieldMappingContext = {
  previousProject?: Project;
};

export const useDeleteFieldMapping = (
  options: UseMutationOptions<
    void,
    APIError,
    DeleteFieldMappingVariables,
    DeleteFieldMappingContext
  > = {},
) => {
  const orgFprint = useCurrentOrgFprint();
  const queryClient = useQueryClient();

  return useMutation<
    void,
    APIError,
    DeleteFieldMappingVariables,
    DeleteFieldMappingContext
  >({
    mutationFn: ({ projectId, mappingId, clearValue }) =>
      fieldMappingService.deleteFieldMapping(
        orgFprint,
        projectId,
        mappingId,
        clearValue,
      ),
    onMutate: async ({ projectId, mappingId }) => {
      return createOptimisticFieldMappingUpdate(
        queryClient,
        projectId,
        (field) => {
          if (field.field_mapping_metadata?.field_mapping_id === mappingId) {
            const { field_mapping_metadata: _, ...fieldWithoutMapping } = field;
            return fieldWithoutMapping;
          }
          return field;
        },
      );
    },
    onError: (err, variables, context) => {
      rollbackOptimisticUpdate(
        queryClient,
        variables.projectId,
        context?.previousProject,
      );
      options.onError?.(err, variables, context);
    },
    onSuccess: (data, variables, context) => {
      options.onSuccess?.(data, variables, context);
    },
  });
};
